import React, { createContext, useContext, useState, useRef, useMemo, useCallback } from 'react';
import { SDS } from '@schibsted-ufo/ui-components';
import { useDispatch } from 'react-redux';
import { showModal as showModalAction, hideModal as hideModalAction } from '@client/redux/modals/actions';

type TModalContextType = {
    openModal: ({ content, trackingType }: {
        content: React.JSX.Element;
        trackingType?: string;
    }) => void;
    closeModal: () => void;
};

const ModalContext = createContext<TModalContextType | null>(null);

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
    const [modalOpened, setModalOpened] = useState(false);
    const [modalContent, setModalContent] = useState<React.JSX.Element | null>(null);
    const activeTrackingType = useRef<string | null>(null);
    const dispatch = useDispatch();

    const openModal = useCallback(({ content, trackingType: type }: {
        content: React.JSX.Element;
        trackingType?: string;
    }) => {
        setModalContent(content);
        setModalOpened(true);
        activeTrackingType.current = type ?? null;
        if (type) {
            dispatch(showModalAction(type));
        }
    }, [dispatch]);

    const closeModal = useCallback(() => {
        if (activeTrackingType.current) {
            dispatch(hideModalAction());
            activeTrackingType.current = null;
        }
        setModalOpened(false);
        setModalContent(null);
    }, [dispatch]);

    const contextValue = useMemo(() => ({
        openModal, closeModal,
    }), [openModal, closeModal]);

    return (
        <ModalContext.Provider value={ contextValue }>
            {children}
            <SDS.Modal isOpen={ modalOpened } onClose={ closeModal } enableBackdropClick enableEscapeKeyDown>
                {modalContent}
            </SDS.Modal>
        </ModalContext.Provider>
    );
};
